import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Notiflix from "notiflix";
import readXlsxFile from "read-excel-file/browser";
import { Form, Nav, ProgressBar, Spinner } from "react-bootstrap";
import {
  currency,
  messageError,
  messageSuccess,
  messageWarning,
  parseSendList,
  url_send_whatsapp_append,
  url_send_whatsapp_create,
  url_send_whatsapp_start,
  url_send_whatsapp_status,
  USER_KEY,
  WHATSAPP_BATCH_SIZE,
  WHATSAPP_UPLOAD_CHUNK_BYTES,
} from "../../helper";

// ແບ່ງລາຍການເປັນກ້ອນຕາມຂະໜາດ JSON ຈິງ — ຂໍ້ຄວາມແຕ່ລະແຖວຍາວບໍ່ເທົ່າກັນ
const splitByBytes = (list, maxBytes) => {
  const chunks = [];
  let current = [];
  let size = 0;

  for (const item of list) {
    const bytes = new Blob([JSON.stringify(item)]).size + 1;
    if (current.length && size + bytes > maxBytes) {
      chunks.push(current);
      current = [];
      size = 0;
    }
    current.push(item);
    size += bytes;
  }
  if (current.length) chunks.push(current);
  return chunks;
};

// ອ່ານ CSV ທັງກ້ອນ — ຮອງຮັບຈຸດພາກ ແລະ ຂຶ້ນແຖວໃໝ່ທີ່ຢູ່ໃນເຄື່ອງໝາຍຄຳເວົ້າ
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      quoted = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n") {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else if (char !== "\r") {
      cell += char;
    }
  }
  row.push(cell);
  rows.push(row);
  return rows;
};

// ຫົວຕາຕະລາງ: phone / ເບີໂທ / number … ແລະ messenger / message / ຂໍ້ຄວາມ …
const PHONE_HEADER = /(phone|number|tel|mobile|ເບີ|เบอร์|โทร|ໂທ)/i;
const MESSAGE_HEADER = /(messenger|message|msg|text|ຂໍ້ຄວາມ|ข้อความ)/i;

// ເບິ່ງວ່າຄ່ານີ້ໜ້າຈະເປັນເບີໂທ ຫຼື ບໍ່ (ຕ້ອງເປັນຕົວເລກລ້ວນ 6–15 ຕົວ)
const isPhoneLike = (value) =>
  /^\+?\d{6,15}$/.test(String(value ?? "").replace(/[\s\-().]/g, ""));

// ແຖວໃນໄຟລ໌ → { phone, message }
// ຫາຖັນເອງ — ຂໍ້ມູນຈະເລີ່ມຖັນໃດ ແຖວໃດກໍ່ໄດ້ (ຕົວຢ່າງ: ຫົວຕາຕະລາງຢູ່ B2/C2)
const rowsToItems = (rows) => {
  const table = (rows || []).map((row) =>
    (row || []).map((cell) => String(cell ?? "").trim().replace(/^"|"$/g, ""))
  );

  // 1) ຫາແຖວຫົວຕາຕະລາງກ່ອນ — ຖ້າພົບ ຈະຮູ້ເລີຍວ່າຖັນໃດເປັນເບີ ຖັນໃດເປັນຂໍ້ຄວາມ
  let headerRow = -1;
  let phoneCol = -1;
  let messageCol = -1;
  for (let i = 0; i < table.length; i++) {
    const index = table[i].findIndex(
      (cell) => cell && PHONE_HEADER.test(cell) && !isPhoneLike(cell)
    );
    if (index < 0) continue;
    headerRow = i;
    phoneCol = index;
    const found = table[i].findIndex(
      (cell, position) => position > index && cell && MESSAGE_HEADER.test(cell)
    );
    messageCol = found >= 0 ? found : index + 1;
    break;
  }

  const items = [];
  for (let i = headerRow + 1; i < table.length; i++) {
    const cells = table[i];
    // ບໍ່ມີຫົວຕາຕະລາງ → ເອົາຊ່ອງທຳອິດທີ່ໜ້າຈະເປັນເບີໂທ
    const column =
      phoneCol >= 0 && isPhoneLike(cells[phoneCol])
        ? phoneCol
        : cells.findIndex((cell) => isPhoneLike(cell));
    if (column < 0) continue;

    const phone = cells[column];
    const message =
      (messageCol > column ? cells[messageCol] : "") ||
      cells.slice(column + 1).find((cell) => cell) ||
      "";
    items.push({ phone, message });
  }
  return items;
};

export default function WhatsAppScreen() {
  const [mode, setMode] = useState("manual"); // manual | excel
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  const [fileName, setFileName] = useState("");
  const [fileItems, setFileItems] = useState([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const fileInput = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    return () => clearTimeout(timer.current);
  }, []);

  const _authHeader = () => ({
    headers: { Authorization: localStorage.getItem(USER_KEY) },
  });

  // ເບີໂທຈາກຊ່ອງພິມ — ແຍກດ້ວຍ enter, ວັກ ຫຼື ຈຸດພາກ
  const manualPhones = useMemo(
    () =>
      manualPhone
        .split(/[\s,;]+/)
        .map((p) => p.trim())
        .filter(Boolean),
    [manualPhone]
  );

  const sendList = mode === "manual" ? manualPhones : fileItems;
  // ແປງເບີໃນເຄື່ອງເລີຍ — ບໍ່ຕ້ອງລໍ backend (backend ຈະແປງຊ້ຳອີກຄັ້ງຕອນສົ່ງ)
  // ⚠️ ໄຟລ໌ເປັນແສນແຖວ — ຕ້ອງ useMemo ບໍ່ດັ່ງນັ້ນຈະຄຳນວນໃໝ່ທຸກຄັ້ງທີ່ພິມ
  const checked = useMemo(() => parseSendList(sendList), [sendList]);
  // ເບີທີ່ມີຂໍ້ຄວາມມາພ້ອມໃນໄຟລ໌ — ສ່ວນທີ່ເຫຼືອຈະໃຊ້ຂໍ້ຄວາມກາງ
  const messageStats = useMemo(() => {
    let withOwn = 0;
    let needDefault = false;
    for (const item of checked.valid) {
      if (item.message) withOwn++;
      else needDefault = true;
    }
    return { withOwn, needDefault };
  }, [checked]);
  const withOwnMessageCount = messageStats.withOwn;
  const needDefaultMessage = messageStats.needDefault;

  // ອ່ານໄຟລ໌ Excel (.xlsx) ຫຼື .csv — ຖັນທຳອິດເປັນເບີໂທ ຖັນທີ 2 ເປັນຂໍ້ຄວາມ
  const _handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileLoading(true);
    setFileName(file.name);
    setFileItems([]);
    try {
      let rows = [];
      if (file.name.toLowerCase().endsWith(".csv")) {
        rows = parseCsv(await file.text());
      } else {
        // read-excel-file ຄືນ [{ sheet, data }] — ເອົາທຸກ sheet ມາລວມກັນ
        const result = await readXlsxFile(file);
        for (const item of result || []) {
          if (Array.isArray(item)) rows.push(item);
          else if (Array.isArray(item?.data)) rows.push(...item.data);
        }
      }

      const list = rowsToItems(rows);
      if (!list.length) {
        messageWarning("ບໍ່ພົບເບີໂທໃນໄຟລ໌");
      }
      setFileItems(list);
    } catch (error) {
      console.log(error);
      messageError("ອ່ານໄຟລ໌ບໍ່ສຳເລັດ — ຮອງຮັບ .xlsx ແລະ .csv");
      setFileName("");
    } finally {
      setFileLoading(false);
    }
  };

  const _clearFile = () => {
    setFileName("");
    setFileItems([]);
    if (fileInput.current) fileInput.current.value = "";
  };

  // ກວດສະຖານະການສົ່ງທຸກ 3 ວິນາທີ
  const _pollStatus = async (jobId) => {
    try {
      const res = await axios.get(
        `${url_send_whatsapp_status}/${jobId}`,
        _authHeader()
      );
      const data = res?.data?.data;
      if (!data) return;
      setJob(data);

      if (data.status === "running") {
        timer.current = setTimeout(() => _pollStatus(jobId), 3000);
        return;
      }

      setLoading(false);
      if (data.status === "done") {
        messageSuccess("ສົ່ງຂໍ້ຄວາມສຳເລັດ");
      } else {
        messageError(data?.error || "ສົ່ງຂໍ້ຄວາມບໍ່ສຳເລັດ");
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      messageError("ກວດສະຖານະການສົ່ງບໍ່ສຳເລັດ");
    }
  };

  const _send = async () => {
    try {
      setLoading(true);
      setJob(null);
      const defaultMessage = message.trim();

      // ມີຂໍ້ຄວາມສະເພາະເບີ → ສົ່ງ items, ບໍ່ມີ → ສົ່ງແຕ່ເບີ (payload ນ້ອຍກວ່າເຄິ່ງ)
      const list = withOwnMessageCount
        ? checked.valid.map((item) => ({
            to: item.to,
            message: item.message || defaultMessage,
          }))
        : checked.valid.map((item) => item.to);
      const key = withOwnMessageCount ? "items" : "numbers";

      // nginx ຈຳກັດ body ~1MB — ແບ່ງເປັນກ້ອນແລ້ວອັບຫຼາຍຮອບ
      const chunks = splitByBytes(list, WHATSAPP_UPLOAD_CHUNK_BYTES);

      // 1) ສ້າງລາຍການສົ່ງ
      const created = await axios.post(
        url_send_whatsapp_create,
        {
          title: title.trim(),
          message: defaultMessage,
          batchSize: WHATSAPP_BATCH_SIZE,
        },
        _authHeader()
      );
      const jobId = created?.data?.data?.jobId;
      if (!jobId) {
        setLoading(false);
        messageWarning(created?.data?.message || "ສ້າງລາຍການສົ່ງບໍ່ສຳເລັດ");
        return;
      }

      // 2) ອັບເບີໂທເປັນກ້ອນ
      setJob({ status: "uploading", total: list.length, sent: 0 });
      let uploaded = 0;
      for (const chunk of chunks) {
        await axios.post(
          url_send_whatsapp_append,
          { jobId, [key]: chunk },
          _authHeader()
        );
        uploaded += chunk.length;
        setJob({ status: "uploading", total: list.length, sent: uploaded });
      }

      // 3) ສັ່ງເລີ່ມສົ່ງ
      const res = await axios.post(
        url_send_whatsapp_start,
        { jobId },
        _authHeader()
      );

      const data = res?.data;
      if (res.status === 200 && data?.success && data?.data?.jobId) {
        const invalid = data?.data?.invalid || [];
        if (invalid.length) {
          messageWarning(`ມີ ${invalid.length} ເບີໃຊ້ບໍ່ໄດ້ ຈຶ່ງຂ້າມໄປ`);
        }
        setJob({
          status: "running",
          total: data?.data?.total || 0,
          sent: 0,
          successCount: 0,
          failureCount: 0,
          errors: [],
        });
        _pollStatus(data.data.jobId);
      } else {
        setLoading(false);
        setJob(null);
        messageWarning(data?.message || "ສົ່ງຂໍ້ຄວາມບໍ່ສຳເລັດ");
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      setJob(null);
      // 413 ມາຈາກ nginx ບໍ່ແມ່ນ API — ບໍ່ມີ message ໃຫ້ອ່ານ ຈຶ່ງບອກເອງ
      if (error?.response?.status === 413) {
        messageError("ຂໍ້ມູນກ້ອນໃຫຍ່ເກີນໄປ — ຫຼຸດ WHATSAPP_UPLOAD_CHUNK_BYTES ລົງ");
        return;
      }
      messageError(error?.response?.data?.message || "ສົ່ງຂໍ້ຄວາມບໍ່ສຳເລັດ");
    }
  };

  const _handleSubmit = (e) => {
    e.preventDefault();
    if (!checked.valid.length) {
      messageWarning("ກະລຸນາປ້ອນເບີໂທ ຫຼື ອັບໂຫຼດໄຟລ໌ກ່ອນ");
      return;
    }
    // ຕ້ອງການຂໍ້ຄວາມກາງສະເພາະຕອນມີເບີທີ່ບໍ່ມີຂໍ້ຄວາມມານຳ
    if (needDefaultMessage && !message.trim()) {
      messageWarning("ກະລຸນາປ້ອນຂໍ້ຄວາມກ່ອນ");
      return;
    }

    // ສົ່ງພ້ອມກັນເທື່ອລະ WHATSAPP_BATCH_SIZE ເບີ ຫ່າງກັນຮອບລະ 1.2 ວິນາທີ
    const rounds = Math.ceil(checked.valid.length / WHATSAPP_BATCH_SIZE);
    const minutes = Math.ceil((rounds * 1.2) / 60);
    const duration =
      minutes >= 60
        ? `${Math.floor(minutes / 60)} ຊົ່ວໂມງ ${minutes % 60} ນາທີ`
        : `${minutes} ນາທີ`;
    Notiflix.Confirm.show(
      "ແຈ້ງເຕືອນ",
      `ຕ້ອງການສົ່ງຂໍ້ຄວາມຫາ ${currency(checked.valid.length)} ເບີ ແທ້ ຫຼື ບໍ່? (ໃຊ້ເວລາປະມານ ${duration})`,
      "ຕົກລົງ",
      "ຍົກເລີກ",
      () => _send()
    );
  };

  const percent = job?.total
    ? Math.round(((job?.sent || 0) / job.total) * 100)
    : 0;

  return (
    <div className="content-inner pb-0 container-fluid" id="page_layout">
      <div className="row justify-content-center">
        <div className="col-lg-8 col-md-10">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">ສົ່ງຂໍ້ຄວາມ WhatsApp</h5>
            </div>
            <div className="card-body">
              <Nav variant="tabs" activeKey={mode} className="mb-3">
                <Nav.Item>
                  <Nav.Link
                    eventKey="manual"
                    disabled={loading}
                    onClick={() => setMode("manual")}
                  >
                    ປ້ອນເບີໂທ
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    eventKey="excel"
                    disabled={loading}
                    onClick={() => setMode("excel")}
                  >
                    ອັບໂຫຼດໄຟລ໌ Excel
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              <Form onSubmit={_handleSubmit}>
                {mode === "manual" ? (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      ເບີໂທ <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder={"ຕົວຢ່າງ:\n92738372\n55512345"}
                      value={manualPhone}
                      disabled={loading}
                      onChange={(e) => setManualPhone(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                      ພິມພຽງເບີກໍ່ໄດ້ (92738372) ລະບົບຈະຕື່ມ 020 ໃຫ້ເອງ — ຫຼາຍເບີໃຫ້ຂຶ້ນແຖວໃໝ່ ຫຼື ຂັ້ນດ້ວຍຈຸດພາກ (,)
                    </Form.Text>
                  </Form.Group>
                ) : (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      ໄຟລ໌ເບີໂທ <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      ref={fileInput}
                      type="file"
                      accept=".xlsx,.csv"
                      disabled={loading || fileLoading}
                      onChange={_handleFile}
                    />
                    <Form.Text className="text-muted">
                      ຮອງຮັບ .xlsx ແລະ .csv — ໃສ່ຫົວຕາຕະລາງ <strong>phone</strong> ແລະ{" "}
                      <strong>messenger</strong> (ຢູ່ຖັນໃດ ແຖວໃດກໍ່ໄດ້).
                      ເບີບໍ່ຕ້ອງມີ 020 ກໍ່ໄດ້ — ແຖວໃດບໍ່ມີຂໍ້ຄວາມ ຈະໃຊ້ຂໍ້ຄວາມກາງຂ້າງລຸ່ມແທນ
                    </Form.Text>
                    {fileLoading ? (
                      <div className="mt-2">
                        <Spinner animation="border" size="sm" className="me-2" />
                        ກຳລັງອ່ານໄຟລ໌...
                      </div>
                    ) : null}
                    {fileName && !fileLoading ? (
                      <div className="d-flex align-items-center gap-2 mt-2">
                        <span className="badge bg-success">
                          {fileName} — {currency(fileItems.length)} ເບີ
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={loading}
                          onClick={_clearFile}
                        >
                          ລຶບ
                        </button>
                      </div>
                    ) : null}
                  </Form.Group>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>
                    ຫົວຂໍ້ <span className="text-muted small">(ບໍ່ຈຳເປັນ)</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="ປ້ອນຫົວຂໍ້ຂໍ້ຄວາມ"
                    value={title}
                    maxLength={1000}
                    disabled={loading}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    {title.length}/1000 — ໃສ່ໃຫ້ທຸກເບີເໝືອນກັນ ແລະ ເປັນ *ຕົວໜາ*
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    ຂໍ້ຄວາມກາງ{" "}
                    {needDefaultMessage ? (
                      <span className="text-danger">*</span>
                    ) : (
                      <span className="text-muted small">(ບໍ່ຈຳເປັນ)</span>
                    )}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="ປ້ອນຂໍ້ຄວາມທີ່ຕ້ອງການສົ່ງ"
                    value={message}
                    maxLength={1000}
                    disabled={loading}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    {message.length}/1000 — ໃຊ້ສະເພາະເບີທີ່ບໍ່ມີຂໍ້ຄວາມມານຳໃນໄຟລ໌
                  </Form.Text>
                </Form.Group>

                {title.trim() || message.trim() ? (
                  <div className="border rounded p-3 mb-3 bg-light">
                    <small className="text-muted d-block mb-1">
                      ຕົວຢ່າງຂໍ້ຄວາມທີ່ຜູ້ຮັບຈະເຫັນ
                    </small>
                    {title.trim() ? (
                      <div className="fw-bold" style={{ whiteSpace: "pre-wrap" }}>
                        {title.trim()}
                      </div>
                    ) : null}
                    <div style={{ whiteSpace: "pre-wrap" }}>
                      {message.trim() || "—"}
                    </div>
                  </div>
                ) : null}

                <div className="alert alert-light border mb-3">
                  <>
                      ຈະສົ່ງຫາ <strong>{currency(checked.valid.length)}</strong> ເບີ
                      {withOwnMessageCount ? (
                        <div className="text-primary small mt-1">
                          ໃຊ້ຂໍ້ຄວາມຈາກໄຟລ໌ {currency(withOwnMessageCount)} ເບີ
                          {needDefaultMessage
                            ? ` · ໃຊ້ຂໍ້ຄວາມກາງ ${currency(
                                checked.valid.length - withOwnMessageCount
                              )} ເບີ`
                            : ""}
                        </div>
                      ) : null}
                      {checked.valid.length ? (
                        <div className="text-muted small mt-1">
                          {checked.valid
                            .slice(0, 5)
                            .map((item) => item.to)
                            .join(", ")}
                          {checked.valid.length > 5
                            ? ` ... ແລະ ອີກ ${currency(checked.valid.length - 5)} ເບີ`
                            : ""}
                        </div>
                      ) : null}
                      {checked.invalid.length ? (
                        <div className="text-danger small mt-1">
                          ໃຊ້ບໍ່ໄດ້ {currency(checked.invalid.length)} ອັນ:{" "}
                          {checked.invalid.slice(0, 5).join(", ")}
                          {checked.invalid.length > 5 ? " ..." : ""}
                        </div>
                      ) : null}
                  </>
                </div>

                <button
                  type="submit"
                  className="btn btn-success w-100 mb-3"
                  disabled={
                    loading ||
                    fileLoading ||
                    !checked.valid.length ||
                    (needDefaultMessage && !message.trim())
                  }
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      ກຳລັງສົ່ງ...
                    </>
                  ) : (
                    "ສົ່ງຂໍ້ຄວາມ"
                  )}
                </button>

                {mode === "excel" && withOwnMessageCount ? (
                  <div className="table-responsive mb-3">
                    <table className="table table-sm table-bordered mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: "35%" }}>ເບີໂທ</th>
                          <th>ຂໍ້ຄວາມ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checked.valid.slice(0, 10).map((item, index) => (
                          <tr key={index}>
                            <td>{item.to}</td>
                            <td className={item.message ? "" : "text-muted"}>
                              {item.message || "— ໃຊ້ຂໍ້ຄວາມກາງ —"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {checked.valid.length > 10 ? (
                      <div className="text-muted small mt-1">
                        ສະແດງ 10 ແຖວທຳອິດ ຈາກ {currency(checked.valid.length)} ແຖວ
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </Form>

              {job ? (
                <div className="mt-4">
                  <div className="d-flex justify-content-between mb-1">
                    <small>
                      {job.status === "uploading"
                        ? "ກຳລັງອັບໂຫຼດເບີໂທ..."
                        : job.status === "running"
                        ? "ກຳລັງສົ່ງ..."
                        : job.status === "done"
                        ? "ສົ່ງສຳເລັດ"
                        : "ເກີດຂໍ້ຜິດພາດ"}
                    </small>
                    <small>
                      {currency(job.sent || 0)} / {currency(job.total || 0)}
                    </small>
                  </div>
                  <ProgressBar
                    now={percent}
                    label={`${percent}%`}
                    animated={job.status === "running" || job.status === "uploading"}
                    variant={
                      job.status === "error"
                        ? "danger"
                        : job.status === "uploading"
                        ? "info"
                        : "success"
                    }
                  />
                  {job.errors?.length ? (
                    <div className="table-responsive mt-3">
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>ເບີໂທ</th>
                            <th>ສາເຫດ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {job.errors.map((item, index) => (
                            <tr key={index}>
                              <td>{item.to}</td>
                              <td className="text-danger small">{item.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}

                  {job.status === "error" ? (
                    <div className="alert alert-danger mt-2 mb-0">{job.error}</div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
